import type { PrinterModelId } from './printerModelConfig';

const LS_PRINT_MODE = 'zeettu.xpP203a.printMode';
const LS_TSPL_SETTINGS = 'zeettu.xpP203a.tsplSettings';

/** Minimum printable label width (mm) — must match payzhe-btp MIN_PRINT_WIDTH_MM. */
export const MIN_XP_P203A_PRINT_WIDTH_MM = 54;

/** Factory self-test defaults for XP-P203A in LABEL mode. */
export const XP_P203A_LABEL_DEFAULTS = {
  widthMm: 55,
  heightMm: 59.25,
  gapMm: 2,
  /** Bluetooth / serial baud from printer self-test (LABEL + RFCOMM). */
  serialBaud: 460800,
  density: 8,
  speed: 4,
  direction: 1 as const,
} as const;

export type XpP203aPrintMode = 'label' | 'receipt';

export type XpP203aLabelConfig = {
  widthMm: number;
  heightMm: number;
  gapMm: number;
  serialBaud: number;
  density: number;
  speed: number;
  direction: 0 | 1;
};

export type XpP203aTsplSettingsOverrides = Partial<
  Pick<XpP203aLabelConfig, 'widthMm' | 'heightMm' | 'gapMm' | 'density' | 'speed' | 'direction'>
>;

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function clampMm(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n * 100) / 100));
}

function readStoredTsplOverrides(): XpP203aTsplSettingsOverrides {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return {};
  try {
    const raw = globalThis.localStorage.getItem(LS_TSPL_SETTINGS);
    if (!raw?.trim()) return {};
    const parsed = JSON.parse(raw) as XpP203aTsplSettingsOverrides;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function envPrintMode(): XpP203aPrintMode | null {
  const raw = String(import.meta.env?.VITE_PRINTER_P203A_PRINT_MODE ?? '').trim().toLowerCase();
  if (raw === 'label' || raw === 'tspl') return 'label';
  if (raw === 'receipt' || raw === 'escpos' || raw === 'esc-pos') return 'receipt';
  return null;
}

function readStoredPrintMode(): XpP203aPrintMode | null {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return null;
  try {
    const raw = globalThis.localStorage.getItem(LS_PRINT_MODE)?.trim().toLowerCase();
    if (raw === 'receipt' || raw === 'escpos') return 'receipt';
    if (raw === 'label' || raw === 'tspl') return 'label';
  } catch {
    /* ignore */
  }
  return null;
}

const printModeListeners = new Set<() => void>();

export function subscribeXpP203aPrintMode(listener: () => void): () => void {
  printModeListeners.add(listener);
  return () => {
    printModeListeners.delete(listener);
  };
}

function notifyXpP203aPrintModeChange(): void {
  for (const listener of printModeListeners) {
    try {
      listener();
    } catch {
      /* ignore */
    }
  }
}

/** Device-saved mode wins over build-time env; default is label (TSPL). */
export function getXpP203aPrintMode(): XpP203aPrintMode {
  const stored = readStoredPrintMode();
  if (stored) return stored;
  const fromEnv = envPrintMode();
  if (fromEnv) return fromEnv;
  return 'label';
}

export function setXpP203aPrintMode(mode: XpP203aPrintMode): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.setItem(LS_PRINT_MODE, mode);
    notifyXpP203aPrintModeChange();
  } catch {
    /* quota */
  }
}

export function clearXpP203aPrintMode(): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.removeItem(LS_PRINT_MODE);
    notifyXpP203aPrintModeChange();
  } catch {
    /* ignore */
  }
}

export function getXpP203aTsplSettingsOverrides(): XpP203aTsplSettingsOverrides {
  return readStoredTsplOverrides();
}

export function saveXpP203aTsplSettingsOverrides(overrides: XpP203aTsplSettingsOverrides): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  const current = readStoredTsplOverrides();
  const next: XpP203aTsplSettingsOverrides = { ...current };
  for (const key of ['widthMm', 'heightMm', 'gapMm', 'density', 'speed', 'direction'] as const) {
    if (key in overrides) {
      const v = overrides[key];
      if (v === undefined || v === null || (typeof v === 'number' && Number.isNaN(v))) {
        delete next[key];
      } else {
        (next as Record<string, unknown>)[key] = v;
      }
    }
  }
  try {
    if (Object.keys(next).length === 0) {
      globalThis.localStorage.removeItem(LS_TSPL_SETTINGS);
    } else {
      globalThis.localStorage.setItem(LS_TSPL_SETTINGS, JSON.stringify(next));
    }
  } catch {
    /* quota */
  }
}

export function clearXpP203aTsplSettingsOverrides(): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.removeItem(LS_TSPL_SETTINGS);
  } catch {
    /* ignore */
  }
}

export function getXpP203aLabelConfig(): XpP203aLabelConfig {
  const d = XP_P203A_LABEL_DEFAULTS;
  const stored = readStoredTsplOverrides();
  const directionRaw = stored.direction ?? d.direction;
  const direction: 0 | 1 = directionRaw === 0 ? 0 : 1;
  return {
    widthMm: clampMm(stored.widthMm, MIN_XP_P203A_PRINT_WIDTH_MM, 80, d.widthMm),
    heightMm: clampMm(stored.heightMm, 10, 200, d.heightMm),
    gapMm: clampMm(stored.gapMm, 0, 10, d.gapMm),
    serialBaud: d.serialBaud,
    density: clampInt(stored.density, 0, 15, d.density),
    speed: clampInt(stored.speed, 1, 10, d.speed),
    direction,
  };
}

/** True when XP-P203A should emit TSPL/TSPL2 ASCII instead of ESC/POS. */
export function shouldUseXpP203aTspl(modelId: PrinterModelId): boolean {
  return modelId === 'xp-p203a' && getXpP203aPrintMode() === 'label';
}
